import React, { useMemo } from "react";
import { JPBReferenceProperty, AreaUnit, PriceMode, AverageCalculationMethod } from "./JPBState";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { Localization } from "@viamap/viamap2-common";
import { formatPricePerSquareMeter, calculateWeightedStatisticsForSeries, getStatisticsLabels, getWeightedValuesForSeries } from "./JPBFunc";
import { PriceRangeSlider } from "./PriceRangeSlider";

type JPBHistogramProps = {
   data: JPBReferenceProperty[];
   allData: JPBReferenceProperty[];
   currentMin?: number;
   currentMax?: number;
   onRangeChange?: (min: number, max: number) => void;
   onResetToSystem?: () => void;
   areaUnit: AreaUnit;
   priceMode: PriceMode;
   averageCalculationMethod: AverageCalculationMethod;
}

export function JPBHistogram(props: JPBHistogramProps) {
   const series = props.priceMode === PriceMode.Landbrug ? "effectiveMark" : "indexed";
   const isWeightedByArea = props.averageCalculationMethod === AverageCalculationMethod.WeightedByArea;
   const columnLabels = useMemo(() => getStatisticsLabels("meanInterval"), []);

   const weightedValues = useMemo(() => {
      return getWeightedValuesForSeries(props.data, series, { averageCalculationMethod: props.averageCalculationMethod });
   }, [props.data, series, props.averageCalculationMethod]);

   const weightedAllValues = useMemo(() => {
      return getWeightedValuesForSeries(props.allData, series, { averageCalculationMethod: props.averageCalculationMethod });
   }, [props.allData, series, props.averageCalculationMethod]);

   const sortedPrices = useMemo(() => weightedValues.map((v) => v.value).sort((a, b) => a - b), [weightedValues]);
   const sortedAllPrices = useMemo(() => weightedAllValues.map((v) => v.value).sort((a, b) => a - b), [weightedAllValues]);

   const allPricesRange = useMemo(() => {
      if (sortedAllPrices.length === 0) return null;

      return {
         min: Math.max(1, Math.min(...sortedAllPrices)),
         max: Math.max(...sortedAllPrices)
      };
   }, [sortedAllPrices]);

   const metrics = useMemo(() => {
      return calculateWeightedStatisticsForSeries(props.data, series, "meanInterval", { averageCalculationMethod: props.averageCalculationMethod });
   }, [props.data, series, props.averageCalculationMethod]);

   const histogramData = useMemo(() => {
      if (sortedPrices.length === 0) return [];

      const minPrice = Math.max(1, Math.min(...sortedPrices));
      const maxPrice = Math.max(...sortedPrices);

      const binStart = Math.floor(minPrice);
      const binEnd = Math.ceil(maxPrice);
      const numBins = binEnd - binStart;

      const bins: number[] = new Array(numBins).fill(0);
      const binLabels: string[] = [];
      const binCenters: number[] = [];
      const binRawStarts: number[] = [];
      const binRawEnds: number[] = [];

      for (let i = 0; i < numBins; i++) {
         const binStartValue = binStart + i;
         const binEndValue = binStart + i + 1;
         const binCenter = binStartValue + 0.5;
         const displayStart = props.areaUnit === AreaUnit.Hectare ? binStartValue * 10000 : binStartValue;
         const displayEnd = props.areaUnit === AreaUnit.Hectare ? binEndValue * 10000 : binEndValue;
         const roundedStart = props.areaUnit === AreaUnit.Hectare ? Math.round(displayStart) : Math.round(displayStart * 100) / 100;
         const roundedEnd = props.areaUnit === AreaUnit.Hectare ? Math.round(displayEnd) : Math.round(displayEnd * 100) / 100;
         const formattedStart = new Intl.NumberFormat(Localization.getLocale(), {
            style: "currency",
            currency: "DKK",
            minimumFractionDigits: props.areaUnit === AreaUnit.Hectare ? 0 : 2,
            maximumFractionDigits: props.areaUnit === AreaUnit.Hectare ? 0 : 2,
            useGrouping: false
         }).format(roundedStart);
         const formattedEnd = new Intl.NumberFormat(Localization.getLocale(), {
            style: "currency",
            currency: "DKK",
            minimumFractionDigits: props.areaUnit === AreaUnit.Hectare ? 0 : 2,
            maximumFractionDigits: props.areaUnit === AreaUnit.Hectare ? 0 : 2,
            useGrouping: false
         }).format(roundedEnd);
         binLabels.push(formattedStart + " - " + formattedEnd);
         binCenters.push(binCenter);
         binRawStarts.push(roundedStart);
         binRawEnds.push(roundedEnd);
      }

      weightedValues.forEach(({ value, weight }) => {
         if (value > 0) {
            let binIndex = Math.floor(value - binStart);
            if (value >= binEnd) {
               binIndex = numBins - 1;
            } else {
               binIndex = Math.max(0, Math.min(binIndex, numBins - 1));
            }
            bins[binIndex] += isWeightedByArea ? (weight / 10000) : 1;
         }
      });

      return bins.map((bucketValue, index) => ({
         range: binLabels[index],
         binCenter: binCenters[index],
         bucketValue,
         rawStart: binRawStarts[index],
         rawEnd: binRawEnds[index]
      }));
   }, [sortedPrices, props.areaUnit, weightedValues, isWeightedByArea]);


   const chartData = histogramData.length > 0 ? histogramData : [];

   const validPricesCount = useMemo(() => {
      return sortedPrices.length;
   }, [sortedPrices]);

   const chartMinMax = useMemo(() => {
      if (chartData.length === 0) return { min: 1, max: 100 };
      if (sortedPrices.length === 0) return { min: 1, max: 100 };
      return {
         min: Math.floor(Math.max(1, Math.min(...sortedPrices))),
         max: Math.ceil(Math.max(...sortedPrices))
      };
   }, [chartData, sortedPrices]);


   const filteredTicks = useMemo(() => {
      if (chartData.length === 0) return [];

      if (sortedPrices.length === 0) return [];

      const minPrice = Math.max(1, Math.min(...sortedPrices));
      const maxPrice = Math.max(...sortedPrices);
      const binStart = Math.floor(minPrice);
      const binEnd = Math.ceil(maxPrice);

      const boundaryTicks: number[] = [];
      for (let i = binStart; i <= binEnd; i++) {
         boundaryTicks.push(i);
      }

      const numBins = binEnd - binStart;
      if (numBins <= 30) {
         return boundaryTicks;
      }

      const targetLabels = 18;
      const step = Math.ceil(boundaryTicks.length / targetLabels);

      const filtered: number[] = [];
      for (let i = 0; i < boundaryTicks.length; i += step) {
         filtered.push(boundaryTicks[i]);
      }
      if (boundaryTicks.length > 0 && filtered[filtered.length - 1] !== boundaryTicks[boundaryTicks.length - 1]) {
         filtered.push(boundaryTicks[boundaryTicks.length - 1]);
      }

      return filtered;
   }, [chartData, sortedPrices]);

   return (
      <div style={{ padding: 8, width: '100%' }}>
         <div className="jpb-card jpb-card--padded">
            {chartData.length > 0 && validPricesCount > 1 ? (
               <ResponsiveContainer
                  width="100%"
                  height={300}
               >
                  <BarChart
                     data={chartData}
                     margin={{ top: 40, right: 20, left: 10, bottom: 10 }}
                  >
                     <CartesianGrid strokeDasharray="3 3" vertical={false} />
                     <XAxis
                        dataKey="binCenter"
                        type="number"
                        angle={-25}
                        dx={10}
                        textAnchor="end"
                        height={40}
                        interval={0}
                        tick={{ fontSize: 10 }}
                        domain={[chartMinMax.min, chartMinMax.max]}
                        ticks={filteredTicks}
                        tickFormatter={(value) => {
                           if (chartData.length === 0) return '';
                           return formatPricePerSquareMeter(value, props.areaUnit, 0, true);
                        }}
                     />
                     <YAxis
                        label={{ value: isWeightedByArea ? Localization.getText("Area (ha)") : Localization.getText("Count"), angle: -90, position: "insideLeft" }}
                        tick={{ fontSize: 10 }}
                        allowDecimals={isWeightedByArea}
                        tickFormatter={(value: number) => isWeightedByArea ? value.toFixed(2) : Math.round(value).toString()}
                     />
                     <Tooltip
                        formatter={(value: number) => [
                           isWeightedByArea ? Number(value).toFixed(2) : value,
                           isWeightedByArea ? Localization.getText("Area (ha)") : Localization.getText("Count")
                        ]}
                        labelFormatter={(label: number) => {
                           const bin = chartData.find(d => Math.abs(d.binCenter - label) < 0.01);
                           if (bin && 'rawStart' in bin && 'rawEnd' in bin && typeof bin.rawStart === 'number' && typeof bin.rawEnd === 'number') {
                              const startValueInM2 = props.areaUnit === AreaUnit.Hectare ? bin.rawStart / 10000 : bin.rawStart;
                              const endValueInM2 = props.areaUnit === AreaUnit.Hectare ? bin.rawEnd / 10000 : bin.rawEnd;
                              const formattedStart = formatPricePerSquareMeter(startValueInM2, props.areaUnit, 0, true);
                              const formattedEnd = formatPricePerSquareMeter(endValueInM2, props.areaUnit, 0, true);
                              return `${Localization.getText("Price interval")}: ${formattedStart} - ${formattedEnd}`;
                           }
                           return formatPricePerSquareMeter(label, props.areaUnit, 0, true);
                        }}
                     />
                     <Bar
                        dataKey="bucketValue"
                        fill="#3a8e87"
                        name={isWeightedByArea ? Localization.getText("Area (ha)") : Localization.getText("Number of properties")}
                        isAnimationActive={true}
                     />
                     {metrics && (
                        <>
                           <ReferenceLine
                              x={metrics.middle}
                              stroke="#ff6b6b"
                              strokeWidth={2}
                              strokeDasharray="5 5"
                              label={{ value: columnLabels.middle, position: "top", fill: "#ff6b6b", fontSize: 11, offset: 18 }}
                           />
                           <ReferenceLine
                              x={metrics.lower}
                              stroke="#4ecdc4"
                              strokeWidth={2}
                              strokeDasharray="5 5"
                              label={{
                                 value: columnLabels.lower,
                                 position: "top",
                                 fill: "#4ecdc4",
                                 fontSize: 11
                              }}
                           />
                           <ReferenceLine
                              x={metrics.upper}
                              stroke="#95e1d3"
                              strokeWidth={2}
                              strokeDasharray="5 5"
                              label={{
                                 value: columnLabels.upper,
                                 position: "top",
                                 fill: "#95e1d3",
                                 fontSize: 11
                              }}
                           />
                        </>
                     )}
                  </BarChart>
               </ResponsiveContainer>
            ) : (
               <div className="jpb-empty-state">
                  <p>{validPricesCount === 1 ? Localization.getText("Not enough data to show") : Localization.getText("No data to display")}</p>
               </div>
            )}
            {allPricesRange && props.currentMin !== undefined && props.currentMax !== undefined && props.onRangeChange && (
               <PriceRangeSlider
                  min={allPricesRange.min}
                  max={allPricesRange.max}
                  currentMin={props.currentMin}
                  currentMax={props.currentMax}
                  onRangeChange={props.onRangeChange}
                  onResetToSystem={props.onResetToSystem}
                  areaUnit={props.areaUnit}
               />
            )}
         </div>
      </div>
   );
}

